import * as THREE from 'three'
import type { VisualParams } from '../../types'
import type { SharedUniforms } from '../AudioUniforms'
import { BasePrimitive } from './Primitive'
import { hsv2rgb, POINT_SPRITE_GLSL } from '../shaderChunks'

/**
 * Projectile rain: a storm of tracers streaking diagonally downward and looping.
 * Each projectile has its own speed; the rain falls faster and brighter with the
 * energy and beat. Pairs well with high Trails for the streak effect.
 */
export class ProjectileRain extends BasePrimitive {
  object3D: THREE.Points
  private material: THREE.ShaderMaterial

  constructor(uniforms: SharedUniforms) {
    super(uniforms)
    this.material = new THREE.ShaderMaterial({
      uniforms: uniforms as unknown as Record<string, THREE.IUniform>,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      vertexShader: /* glsl */ `
        attribute vec3 aStart;   // x, phase offset, z
        attribute float aSeed;
        uniform float uTime, uBass, uBeat, uLevel, uReactivity, uBassPunch, uPixelRatio, uPointScale;
        varying float vLife;
        varying float vSeed;
        void main() {
          // Steady fall speed; the music only nudges it a little (no runaway).
          float speed = 0.22 + uLevel * 0.08;
          float p = fract(aStart.y + uTime * speed * (0.6 + aSeed * 0.9));
          float y = mix(8.5, -8.5, p);            // fall from top to bottom
          float x = aStart.x + p * 2.4;           // diagonal drift (projectile)
          vec3 pos = vec3(x, y, aStart.z);
          vLife = 1.0 - p;                        // fade toward the bottom
          vSeed = aSeed;
          vec4 mv = modelViewMatrix * vec4(pos, 1.0);
          gl_Position = projectionMatrix * mv;
          float px = (2.4 + uBeat * 3.0 + uBass * uBassPunch * 2.0) * uReactivity;
          gl_PointSize = clamp(px * (6.0 / -mv.z), 1.0, 18.0) * uPixelRatio * uPointScale;
        }
      `,
      fragmentShader: /* glsl */ `
        precision highp float;
        uniform float uHue, uTreble, uLevel;
        varying float vLife;
        varying float vSeed;
        ${hsv2rgb}
        ${POINT_SPRITE_GLSL}
        void main() {
          float core = pointMask();
          if (core < 0.01) discard;
          // Warm tracer colour, drifting slightly per projectile and with treble.
          float hue = fract(uHue + vSeed * 0.06 + uTreble * 0.06);
          vec3 col = hsv2rgb(vec3(hue, 0.8, 0.7 + uLevel * 0.3));
          float a = core * (uPointSolid > 0.5 ? 1.0 : (0.35 + vLife * 0.6));
          gl_FragColor = vec4(col, a);
        }
      `,
    })
    this.object3D = new THREE.Points(new THREE.BufferGeometry(), this.material)
    this.object3D.frustumCulled = false
    this.rebuild({ density: 1 } as VisualParams)
  }

  update(params: VisualParams): void {
    const solid = params.pointSolid === true
    this.material.blending = solid ? THREE.NormalBlending : THREE.AdditiveBlending
    this.material.depthWrite = solid
  }

  rebuild(params: VisualParams): void {
    const count = Math.floor(9000 * (params.density ?? 1))
    const positions = new Float32Array(count * 3)
    const start = new Float32Array(count * 3)
    const seed = new Float32Array(count)
    for (let i = 0; i < count; i++) {
      const x = (Math.random() * 2 - 1) * 9
      const z = (Math.random() * 2 - 1) * 6
      start[i * 3] = x
      start[i * 3 + 1] = Math.random() // phase offset
      start[i * 3 + 2] = z
      seed[i] = Math.random()
      positions[i * 3] = x
      positions[i * 3 + 1] = 0
      positions[i * 3 + 2] = z
    }
    const geo = this.object3D.geometry
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3))
    geo.setAttribute('aStart', new THREE.BufferAttribute(start, 3))
    geo.setAttribute('aSeed', new THREE.BufferAttribute(seed, 1))
    geo.computeBoundingSphere()
  }
}
